// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { consoleService } from '../../services/consoleService';
import { 
  DashboardOverview, 
  AnalyticsFilters, 
  ExportOptions,
  GameMetrics,
  UserMetrics,
  CoinMetrics,
  AdMetrics
} from '../../types/analytics';
import './Analytics.scss';

const Analytics: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState<'today' | 'week' | 'month' | 'custom'>('week');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  // Fetch analytics data
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: AnalyticsFilters = {
        dateRange: getDateRange(),
        userSegment: 'all',
        platform: 'all'
      };

      const response = await consoleService.getAnalyticsDashboard(filters);
      setDashboardData(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Get date range based on selection
  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (selectedTimeRange) {
      case 'today':
        return {
          startDate: today.toISOString(),
          endDate: now.toISOString()
        };
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return {
          startDate: weekAgo.toISOString(),
          endDate: now.toISOString()
        };
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return {
          startDate: monthAgo.toISOString(),
          endDate: now.toISOString()
        };
      case 'custom':
        return {
          startDate: customDateRange.startDate,
          endDate: customDateRange.endDate
        };
      default:
        return {
          startDate: today.toISOString(),
          endDate: now.toISOString()
        };
    }
  };

  // Export data
  const handleExport = async (format: 'excel' | 'pdf' | 'csv') => {
    try {
      const exportOptions: ExportOptions = {
        format,
        dateRange: getDateRange(),
        metrics: ['users', 'games', 'coins', 'ads'],
        includeCharts: format === 'pdf'
      };

      const filters: AnalyticsFilters = {
        dateRange: getDateRange(),
        userSegment: 'all',
        platform: 'all'
      };

      const result = await consoleService.exportAnalytics(exportOptions, filters);
      
      if (result.downloadUrl) {
        // Create download link
        const link = document.createElement('a');
        link.href = result.downloadUrl;
        link.download = `investaplay-analytics-${Date.now()}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export data');
    }
  };

  // Auto-refresh functionality
  useEffect(() => {
    fetchAnalytics();

    // Set up auto-refresh every 5 minutes
    const interval = setInterval(fetchAnalytics, 5 * 60 * 1000);
    setRefreshInterval(interval);

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [selectedTimeRange, customDateRange]);

  // Format numbers for display
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="analytics-header">
          <h2 className="analytics-title">Analytics Dashboard</h2>
        </div>
        <div className="loading-state">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading analytics...</span>
          </div>
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="analytics-header">
          <h2 className="analytics-title">Analytics Dashboard</h2>
        </div>
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
          <button className="btn btn-primary ms-3" onClick={fetchAnalytics}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="analytics-container">
        <div className="analytics-header">
          <h2 className="analytics-title">Analytics Dashboard</h2>
        </div>
        <div className="alert alert-info">
          No analytics data available.
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <div className="header-content">
          <h2 className="analytics-title">Investaplay Analytics Dashboard</h2>
          <p className="analytics-subtitle">
            Comprehensive insights into user engagement, game performance, and revenue metrics
          </p>
        </div>
        
        <div className="header-actions">
          <div className="time-range-selector">
            <select 
              value={selectedTimeRange} 
              onChange={(e) => setSelectedTimeRange(e.target.value as any)}
              className="form-select"
            >
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          
          {selectedTimeRange === 'custom' && (
            <div className="custom-date-range">
              <input
                type="date"
                value={customDateRange.startDate}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="form-control"
              />
              <span>to</span>
              <input
                type="date"
                value={customDateRange.endDate}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="form-control"
              />
            </div>
          )}
          
          <div className="export-buttons">
            <button className="btn btn-outline-success" onClick={() => handleExport('excel')}>
              📊 Excel
            </button>
            <button className="btn btn-outline-danger" onClick={() => handleExport('pdf')}>
              📄 PDF
            </button>
            <button className="btn btn-outline-secondary" onClick={() => handleExport('csv')}>
              📋 CSV
            </button>
          </div>
          
          <button className="btn btn-primary" onClick={fetchAnalytics}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="metrics-grid">
        {/* User Metrics */}
        <div className="metric-card user-metrics">
          <div className="metric-header">
            <h3>👥 User Metrics</h3>
            <span className="metric-icon">📈</span>
          </div>
          <div className="metric-stats">
            <div className="stat-item primary">
              <span className="stat-value">{formatNumber(dashboardData.userMetrics.totalUsers)}</span>
              <span className="stat-label">Total Users</span>
            </div>
            <div className="stat-item success">
              <span className="stat-value">{formatNumber(dashboardData.userMetrics.activeUsers)}</span>
              <span className="stat-label">Active Users</span>
            </div>
            <div className="stat-item warning">
              <span className="stat-value">{formatNumber(dashboardData.userMetrics.dailyActiveUsers)}</span>
              <span className="stat-label">Daily Active</span>
            </div>
            <div className="stat-item info">
              <span className="stat-value">{formatNumber(dashboardData.userMetrics.newUsersToday)}</span>
              <span className="stat-label">New Today</span>
            </div>
          </div>
        </div>

        {/* Game Metrics */}
        <div className="metric-card game-metrics">
          <div className="metric-header">
            <h3>🎮 Game Performance</h3>
            <span className="metric-icon">🏆</span>
          </div>
          <div className="metric-stats">
            {dashboardData.gameMetrics.slice(0, 3).map((game) => (
              <div key={game.gameId} className="game-stat">
                <div className="game-name">{game.gameName}</div>
                <div className="game-plays">{formatNumber(game.totalPlays)} plays</div>
                <div className="game-players">{formatNumber(game.uniquePlayers)} players</div>
                <div className="game-session">{game.averageSessionLength.toFixed(1)}min avg</div>
              </div>
            ))}
          </div>
        </div>

        {/* Coin Economy */}
        <div className="metric-card coin-metrics">
          <div className="metric-header">
            <h3>🪙 Coin Economy</h3>
            <span className="metric-icon">💰</span>
          </div>
          <div className="metric-stats">
            <div className="stat-item primary">
              <span className="stat-value">{formatNumber(dashboardData.coinMetrics.totalCoinsInCirculation)}</span>
              <span className="stat-label">Total Coins</span>
            </div>
            <div className="stat-item success">
              <span className="stat-value">{formatNumber(dashboardData.coinMetrics.totalCoinsEarned)}</span>
              <span className="stat-label">Coins Earned</span>
            </div>
            <div className="stat-item warning">
              <span className="stat-value">{formatNumber(dashboardData.coinMetrics.totalCoinsSpent)}</span>
              <span className="stat-label">Coins Spent</span>
            </div>
            <div className="stat-item info">
              <span className="stat-value">{formatNumber(dashboardData.coinMetrics.averageCoinsPerUser)}</span>
              <span className="stat-label">Avg per User</span>
            </div>
          </div>
        </div>

        {/* Ad Revenue */}
        <div className="metric-card ad-metrics">
          <div className="metric-header">
            <h3>📺 Ad Performance</h3>
            <span className="metric-icon">💵</span>
          </div>
          <div className="metric-stats">
            <div className="stat-item primary">
              <span className="stat-value">{formatCurrency(dashboardData.adMetrics.adRevenue)}</span>
              <span className="stat-label">Revenue</span>
            </div>
            <div className="stat-item success">
              <span className="stat-value">{formatNumber(dashboardData.adMetrics.totalAdViews)}</span>
              <span className="stat-label">Total Views</span>
            </div>
            <div className="stat-item warning">
              <span className="stat-value">{formatNumber(dashboardData.adMetrics.rewardedAdViews)}</span>
              <span className="stat-label">Rewarded Ads</span>
            </div>
            <div className="stat-item info">
              <span className="stat-value">{formatPercentage(dashboardData.adMetrics.adClickThroughRate)}</span>
              <span className="stat-label">CTR</span>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Game Analytics */}
      <div className="detailed-analytics">
        <div className="section-header">
          <h3>🎯 Detailed Game Analytics</h3>
        </div>
        
        <div className="games-table-container">
          <table className="games-table">
            <thead>
              <tr>
                <th>Game</th>
                <th>Total Plays</th>
                <th>Unique Players</th>
                <th>Avg Session</th>
                <th>Completion Rate</th>
                <th>Single Player</th>
                <th>Multiplayer</th>
                <th>Top Score</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.gameMetrics.map((game) => (
                <tr key={game.gameId} className="game-row">
                  <td className="game-name-cell">
                    <div className="game-info">
                      <span className="game-icon">
                        {game.gameName === 'Ludo' ? '🎲' : 
                         game.gameName === 'Quiz' ? '🧠' : 
                         game.gameName === 'Solitaire' ? '🃏' : '🎮'}
                      </span>
                      <span className="game-name">{game.gameName}</span>
                    </div>
                  </td>
                  <td>{formatNumber(game.totalPlays)}</td>
                  <td>{formatNumber(game.uniquePlayers)}</td>
                  <td>{game.averageSessionLength.toFixed(1)}min</td>
                  <td>
                    <span className={`completion-rate ${game.completionRate >= 70 ? 'high' : game.completionRate >= 40 ? 'medium' : 'low'}`}>
                      {formatPercentage(game.completionRate)}
                    </span>
                  </td>
                  <td>{formatNumber(game.singlePlayerGames)}</td>
                  <td>{formatNumber(game.multiPlayerGames)}</td>
                  <td className="top-score">{formatNumber(game.topScore)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-activity">
        <div className="section-header">
          <h3>📊 Recent Activity</h3>
        </div>
        
        <div className="activity-list">
          {dashboardData.recentActivity.slice(0, 10).map((activity) => (
            <div key={activity.id} className="activity-item">
              <div className="activity-time">
                {new Date(activity.timestamp).toLocaleTimeString()}
              </div>
              <div className="activity-content">
                <span className="activity-user">{activity.username}</span>
                <span className="activity-action">{activity.action}</span>
                {activity.gameName && (
                  <span className="activity-game">in {activity.gameName}</span>
                )}
                {activity.coinsInvolved && (
                  <span className="activity-coins">({activity.coinsInvolved} coins)</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Performers */}
      <div className="top-performers">
        <div className="section-header">
          <h3>🏆 Top Performers</h3>
        </div>
        
        <div className="performers-grid">
          <div className="performer-card">
            <h4>🪙 Top Coin Earner</h4>
            <div className="performer-info">
              <div className="performer-name">{dashboardData.coinMetrics.topCoinEarner.username}</div>
              <div className="performer-value">{formatNumber(dashboardData.coinMetrics.topCoinEarner.coins)} coins</div>
            </div>
          </div>
          
          <div className="performer-card">
            <h4>🌟 Global Leader</h4>
            <div className="performer-info">
              <div className="performer-name">{dashboardData.leaderboardMetrics.globalLeaderboard.topPlayer.username}</div>
              <div className="performer-value">{formatNumber(dashboardData.leaderboardMetrics.globalLeaderboard.topPlayer.totalCoins)} coins</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
